import { useState, useMemo, useCallback, useEffect } from 'react'
import * as joint from '@joint/core'
import C4DiagramCanvas from './C4DiagramCanvas'
import {
  StructurizrWorkspace,
  ViewInfo,
  getAllViews,
  buildElementMap,
  buildRelationshipMap,
  createShapeForElement,
  createLinksForView,
  applyAutomaticLayout,
  resolveViewElements,
  resolveDeploymentNodes,
  createBoundary,
  calculateBoundaryDimensions,
  createDeploymentNode,
  createInfrastructureNode,
  createContainer,
  createSoftwareSystem,
  ContainerView,
  ComponentView,
  DeploymentView,
  DeploymentNodeInfo,
} from '../lib/jointjs'

interface JointJSDiagramViewerProps {
  workspaceJson: string
}

/**
 * JointJS-based viewer for native C4 diagrams
 * Renders directly from workspace JSON without export conversion
 */
export default function JointJSDiagramViewer({
  workspaceJson,
}: JointJSDiagramViewerProps) {
  const [selectedViewIndex, setSelectedViewIndex] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null)

  // Parse workspace JSON
  const workspace = useMemo<StructurizrWorkspace | null>(() => {
    try {
      setError(null)
      return JSON.parse(workspaceJson) as StructurizrWorkspace
    } catch (err) {
      setError('Failed to parse workspace JSON')
      return null
    }
  }, [workspaceJson])

  // Get all available views
  const views = useMemo<ViewInfo[]>(() => {
    if (!workspace?.views) return []
    return getAllViews(workspace.views)
  }, [workspace])

  // Reset selected view index when views change
  useEffect(() => {
    if (views.length > 0 && selectedViewIndex >= views.length) {
      setSelectedViewIndex(0)
    }
  }, [views, selectedViewIndex])

  // Build element and relationship maps
  const { elementMap, relationshipMap } = useMemo(() => {
    if (!workspace?.model) {
      return { elementMap: new Map(), relationshipMap: new Map() }
    }
    return {
      elementMap: buildElementMap(workspace.model),
      relationshipMap: buildRelationshipMap(workspace.model),
    }
  }, [workspace])

  // Create JointJS graph for current view
  const graph = useMemo(() => {
    const g = new joint.dia.Graph()

    if (!workspace || views.length === 0) return g

    const currentView = views[selectedViewIndex]
    if (!currentView) return g

    const { view, type: viewType } = currentView
    const configuration = workspace.views?.configuration

    // Resolve elements to show - handles empty elements arrays
    const { elements: elementViews, relationships: relationshipViews } = resolveViewElements(
      view,
      viewType,
      workspace.model,
      elementMap,
      relationshipMap
    )

    // Create elements
    const cells: joint.dia.Cell[] = []
    const elementIds = new Set<string>()
    const elementShapes = new Map<string, joint.dia.Element>()

    for (const elementView of elementViews) {
      const elementInfo = elementMap.get(elementView.id)
      if (!elementInfo) continue

      elementIds.add(elementView.id)
      const shape = createShapeForElement(elementInfo, elementView, configuration)
      cells.push(shape)
      elementShapes.set(elementView.id, shape)
    }

    // Apply automatic layout if positions are all 0,0
    const shapes = cells.filter(cell => cell instanceof joint.dia.Element) as joint.dia.Element[]
    const needsLayout = shapes.every(s => {
      const pos = s.position()
      return pos.x === 0 && pos.y === 0
    })
    if (needsLayout && shapes.length > 0) {
      applyAutomaticLayout(shapes)
    }

    // Add boundary boxes for Container and Component views
    if (viewType === 'Container') {
      const containerView = view as ContainerView
      const targetSystem = workspace.model.softwareSystems?.find(
        ss => ss.id === containerView.softwareSystemId
      )
      if (targetSystem) {
        // Collect positions of containers belonging to this system
        const containerPositions: Array<{ x: number; y: number; width: number; height: number }> = []
        targetSystem.containers?.forEach(container => {
          const shape = elementShapes.get(container.id)
          if (shape) {
            const pos = shape.position()
            const size = shape.size()
            containerPositions.push({
              x: pos.x,
              y: pos.y,
              width: size.width,
              height: size.height,
            })
          }
        })

        if (containerPositions.length > 0) {
          const boundaryDims = calculateBoundaryDimensions(containerPositions)
          const boundary = createBoundary(
            targetSystem.id,
            targetSystem.name,
            'SoftwareSystem',
            boundaryDims.x,
            boundaryDims.y,
            boundaryDims.width,
            boundaryDims.height
          )
          // Add boundary first so it's behind other elements
          cells.unshift(boundary)
        }
      }
    } else if (viewType === 'Component') {
      const componentView = view as ComponentView
      // Find the container and its parent system
      let targetContainer = null
      let parentSystem = null
      for (const ss of workspace.model.softwareSystems || []) {
        const container = ss.containers?.find(c => c.id === componentView.containerId)
        if (container) {
          targetContainer = container
          parentSystem = ss
          break
        }
      }

      if (targetContainer && parentSystem) {
        // Collect positions of components belonging to this container
        const componentPositions: Array<{ x: number; y: number; width: number; height: number }> = []
        targetContainer.components?.forEach(component => {
          const shape = elementShapes.get(component.id)
          if (shape) {
            const pos = shape.position()
            const size = shape.size()
            componentPositions.push({
              x: pos.x,
              y: pos.y,
              width: size.width,
              height: size.height,
            })
          }
        })

        if (componentPositions.length > 0) {
          const boundaryDims = calculateBoundaryDimensions(componentPositions)
          const boundary = createBoundary(
            targetContainer.id,
            targetContainer.name,
            'Container',
            boundaryDims.x,
            boundaryDims.y,
            boundaryDims.width,
            boundaryDims.height
          )
          cells.unshift(boundary)
        }
      }
    } else if (viewType === 'Deployment') {
      // Handle deployment view - render nested deployment nodes
      const deploymentView = view as DeploymentView
      const deploymentNodes = resolveDeploymentNodes(deploymentView, workspace.model, elementMap)

      let xOffset = 50
      let yOffset = 50

      function renderDeploymentNode(
        node: DeploymentNodeInfo,
        x: number,
        y: number,
        depth: number = 0
      ): { width: number; height: number } {
        const padding = 30
        const headerHeight = 50
        const instanceSpacing = 260

        // Calculate content dimensions first
        let contentWidth = 0
        let contentHeight = 0

        // Render infrastructure nodes inside this deployment node
        let infraX = x + padding
        node.infrastructureNodes.forEach(infra => {
          const infraNode = createInfrastructureNode(
            infra.id,
            infra.name,
            infra.technology,
            infra.description,
            infraX,
            y + headerHeight + padding
          )
          cells.push(infraNode)
          elementIds.add(infra.id)
          elementShapes.set(infra.id, infraNode)
          infraX += instanceSpacing
          contentWidth = Math.max(contentWidth, infraX - x)
        })

        // Render software system instances
        node.softwareSystemInstances.forEach(instance => {
          const systemInfo = elementMap.get(instance.softwareSystemId)
          const systemNode = createSoftwareSystem(
            instance.id,
            instance.name,
            systemInfo?.description || '',
            infraX,
            y + headerHeight + padding,
            false
          )
          cells.push(systemNode)
          elementIds.add(instance.id)
          elementShapes.set(instance.id, systemNode)
          infraX += instanceSpacing
          contentWidth = Math.max(contentWidth, infraX - x)
        })

        // Render container instances
        node.containerInstances.forEach(instance => {
          let containerInfo = null
          for (const ss of workspace!.model.softwareSystems || []) {
            const container = ss.containers?.find(c => c.id === instance.containerId)
            if (container) {
              containerInfo = container
              break
            }
          }
          const containerNode = createContainer(
            instance.id,
            instance.name,
            containerInfo?.technology || '',
            containerInfo?.description || '',
            infraX,
            y + headerHeight + padding
          )
          cells.push(containerNode)
          elementIds.add(instance.id)
          elementShapes.set(instance.id, containerNode)
          infraX += instanceSpacing
          contentWidth = Math.max(contentWidth, infraX - x)
        })

        contentHeight = node.infrastructureNodes.length > 0 ||
                       node.softwareSystemInstances.length > 0 ||
                       node.containerInstances.length > 0
          ? 200
          : 50

        // Render child deployment nodes
        let childX = x + padding
        let maxChildHeight = 0
        node.children.forEach(child => {
          const childDims = renderDeploymentNode(
            child,
            childX,
            y + headerHeight + contentHeight + padding,
            depth + 1
          )
          childX += childDims.width + padding
          maxChildHeight = Math.max(maxChildHeight, childDims.height)
          contentWidth = Math.max(contentWidth, childX - x)
        })

        if (node.children.length > 0) {
          contentHeight += maxChildHeight + padding
        }

        // Create the deployment node box
        const nodeWidth = Math.max(400, contentWidth + padding)
        const nodeHeight = Math.max(200, headerHeight + contentHeight + padding)

        const deploymentNodeShape = createDeploymentNode(
          node.id,
          node.name,
          node.technology,
          node.instances,
          x,
          y,
          nodeWidth,
          nodeHeight
        )
        cells.unshift(deploymentNodeShape) // Add behind content
        elementIds.add(node.id)
        elementShapes.set(node.id, deploymentNodeShape)

        return { width: nodeWidth, height: nodeHeight }
      }

      // Render all top-level deployment nodes
      deploymentNodes.forEach(node => {
        const dims = renderDeploymentNode(node, xOffset, yOffset)
        xOffset += dims.width + 50
      })
    }

    // Create links (with sequence numbers for dynamic views)
    const links = createLinksForView(
      relationshipViews,
      relationshipMap,
      elementIds,
      configuration,
      viewType
    )
    cells.push(...links)

    // Add all cells to graph
    g.addCells(cells)

    return g
  }, [workspace, views, selectedViewIndex, elementMap, relationshipMap])

  // Handle element click
  const handleElementClick = useCallback((_elementId: string) => {
    // Element click handling - details panel is shown via onElementSelect
  }, [])

  if (error) {
    return (
      <div className="h-full flex items-center justify-center text-red-500">
        <p>{error}</p>
      </div>
    )
  }

  if (!workspace) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <p>No workspace data available. Parse your DSL first.</p>
      </div>
    )
  }

  if (views.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <p>No views defined in the workspace.</p>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* View tabs */}
      <div className="flex border-b border-gray-200 overflow-x-auto flex-shrink-0">
        {views.map((viewInfo, index) => (
          <button
            key={viewInfo.key}
            onClick={() => setSelectedViewIndex(index)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
              selectedViewIndex === index
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {viewInfo.title}
            <span className="ml-1 text-xs text-gray-400">({viewInfo.type})</span>
          </button>
        ))}
      </div>

      {/* Diagram canvas and details panel */}
      <div className="flex-1 min-h-0 flex">
        <div className={`${selectedElementId ? 'flex-1' : 'w-full'} min-w-0`}>
          <C4DiagramCanvas
            graph={graph}
            onElementClick={handleElementClick}
            onElementSelect={setSelectedElementId}
            diagramName={views[selectedViewIndex]?.title || 'diagram'}
          />
        </div>
        {selectedElementId && (
          <ElementDetailsPanel
            elementId={selectedElementId}
            elementMap={elementMap}
            relationshipMap={relationshipMap}
            onClose={() => setSelectedElementId(null)}
          />
        )}
      </div>
    </div>
  )
}

/**
 * Element Details Panel - shows information about selected element
 */
interface ElementDetailsPanelProps {
  elementId: string
  elementMap: Map<string, import('../lib/jointjs').ElementInfo>
  relationshipMap: Map<string, import('../lib/jointjs').Relationship>
  onClose: () => void
}

function ElementDetailsPanel({
  elementId,
  elementMap,
  relationshipMap,
  onClose,
}: ElementDetailsPanelProps) {
  const element = elementMap.get(elementId)

  if (!element) {
    return null
  }

  // Find relationships involving this element
  const outgoingRelationships: Array<{ rel: import('../lib/jointjs').Relationship; target: string }> = []
  const incomingRelationships: Array<{ rel: import('../lib/jointjs').Relationship; source: string }> = []

  relationshipMap.forEach(rel => {
    if (rel.sourceId === elementId) {
      const targetElement = elementMap.get(rel.destinationId)
      if (targetElement) {
        outgoingRelationships.push({ rel, target: targetElement.name })
      }
    } else if (rel.destinationId === elementId) {
      const sourceElement = elementMap.get(rel.sourceId)
      if (sourceElement) {
        incomingRelationships.push({ rel, source: sourceElement.name })
      }
    }
  })

  return (
    <div className="w-80 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-800 truncate">{element.name}</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded text-gray-500"
          title="Close"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Type */}
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Type</span>
          <p className="text-sm text-gray-700 mt-1">{element.type}</p>
        </div>

        {/* Technology */}
        {element.technology && (
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Technology</span>
            <p className="text-sm text-gray-700 mt-1">{element.technology}</p>
          </div>
        )}

        {/* Description */}
        {element.description && (
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Description</span>
            <p className="text-sm text-gray-700 mt-1">{element.description}</p>
          </div>
        )}

        {/* Tags */}
        {element.tags && (
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {element.tags.split(',').map((tag, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                >
                  {tag.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Outgoing Relationships */}
        {outgoingRelationships.length > 0 && (
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Outgoing Relationships ({outgoingRelationships.length})
            </span>
            <ul className="mt-1 space-y-1">
              {outgoingRelationships.map(({ rel, target }) => (
                <li key={rel.id} className="text-sm">
                  <span className="text-gray-500">→</span>{' '}
                  <span className="font-medium text-gray-700">{target}</span>
                  {rel.description && (
                    <span className="text-gray-500 text-xs block ml-3">{rel.description}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Incoming Relationships */}
        {incomingRelationships.length > 0 && (
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Incoming Relationships ({incomingRelationships.length})
            </span>
            <ul className="mt-1 space-y-1">
              {incomingRelationships.map(({ rel, source }) => (
                <li key={rel.id} className="text-sm">
                  <span className="text-gray-500">←</span>{' '}
                  <span className="font-medium text-gray-700">{source}</span>
                  {rel.description && (
                    <span className="text-gray-500 text-xs block ml-3">{rel.description}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
